/**
 * Decide which viewer to render for a given filename / mime / kind.
 *
 * The server already returns a `kind` ("markdown", "code", "image",
 * "text", "binary", "stub") inside `/fs/read` responses, but we still
 * need to dispatch to the right component up-front (without a server
 * round-trip) so the tree-click → viewer-open animation feels instant.
 *
 * Resolution order:
 *   1. Extension lookup (cheap, deterministic, works offline).
 *   2. MIME prefix (when the server provides one).
 *   3. Server `kind` (when /fs/read response has already landed).
 */

export type ViewerKind =
  | 'markdown'
  | 'code'
  | 'text'
  | 'html'
  | 'image'
  | 'pdf'
  | 'video'
  | 'audio'
  | 'directory'
  | 'binary';

const EXT_TO_KIND: ReadonlyMap<string, ViewerKind> = new Map<string, ViewerKind>([
  // Markdown
  ['md', 'markdown'],
  ['mdx', 'markdown'],
  ['markdown', 'markdown'],

  // Code (highlighted)
  ['ts', 'code'],
  ['tsx', 'code'],
  ['js', 'code'],
  ['jsx', 'code'],
  ['mjs', 'code'],
  ['cjs', 'code'],
  ['py', 'code'],
  ['rb', 'code'],
  ['go', 'code'],
  ['rs', 'code'],
  ['java', 'code'],
  ['kt', 'code'],
  ['swift', 'code'],
  ['c', 'code'],
  ['h', 'code'],
  ['cpp', 'code'],
  ['hpp', 'code'],
  ['sh', 'code'],
  ['bash', 'code'],
  ['zsh', 'code'],
  ['css', 'code'],
  ['scss', 'code'],
  ['xml', 'code'],
  ['sql', 'code'],
  ['json', 'code'],
  ['yaml', 'code'],
  ['yml', 'code'],
  ['toml', 'code'],
  ['ini', 'code'],
  ['conf', 'code'],

  // Plain text
  ['txt', 'text'],
  ['log', 'text'],
  ['env', 'text'],

  // Images
  ['png', 'image'],
  ['jpg', 'image'],
  ['jpeg', 'image'],
  ['gif', 'image'],
  ['webp', 'image'],
  ['svg', 'image'],
  ['avif', 'image'],
  ['bmp', 'image'],

  // PDF
  ['pdf', 'pdf'],

  // HTML — rendered in a sandboxed iframe (not source-highlighted).
  ['html', 'html'],
  ['htm', 'html'],

  // Video / audio. Kept in step with MEDIA_MIME in
  // apps/chaos-gateway/src/lib/media-range.ts — a format the server can type
  // but the viewer cannot route just lands in the binary viewer saying the
  // file cannot be previewed, which is not true.
  ['mp4', 'video'],
  ['m4v', 'video'],
  ['webm', 'video'],
  ['mov', 'video'],
  ['mkv', 'video'],
  ['avi', 'video'],
  ['ogv', 'video'],
  ['3gp', 'video'],
  ['mpeg', 'video'],
  ['mpg', 'video'],
  ['mp3', 'audio'],
  ['wav', 'audio'],
  ['ogg', 'audio'],
  ['oga', 'audio'],
  ['opus', 'audio'],
  ['weba', 'audio'],
  ['flac', 'audio'],
  ['m4a', 'audio'],
  ['aac', 'audio'],
  ['aiff', 'audio'],
  ['aif', 'audio'],
  ['amr', 'audio'],
  ['wma', 'audio'],
]);

function extensionFromName(name: string): string {
  const dot = name.lastIndexOf('.');
  if (dot < 0 || dot === name.length - 1) return '';
  return name.slice(dot + 1).toLowerCase();
}

interface ViewerKindInput {
  name: string;
  kind?: 'dir' | 'file' | 'symlink' | 'other' | 'unknown' | undefined;
  mime?: string | null | undefined;
}

export function getViewerKind(input: ViewerKindInput): ViewerKind {
  if (input.kind === 'dir') return 'directory';

  const mime = input.mime ?? '';
  // A caller holding a real media MIME knows more than the extension does:
  // `.webm` is in the table as video, but a voice note is audio/webm and
  // rendering it as video shows a black rectangle. Observed metadata wins
  // over the guess from the filename.
  if (mime.startsWith('audio/')) return 'audio';
  if (mime.startsWith('video/')) return 'video';

  const ext = extensionFromName(input.name);
  const fromExt = ext ? EXT_TO_KIND.get(ext) : undefined;
  if (fromExt) return fromExt;

  if (mime.startsWith('image/')) return 'image';
  if (mime === 'application/pdf') return 'pdf';
  if (mime.startsWith('text/markdown')) return 'markdown';
  if (mime.startsWith('text/html') || mime === 'application/xhtml+xml') return 'html';
  if (mime.startsWith('text/')) return 'text';
  if (mime.startsWith('application/json')) return 'code';
  if (mime.startsWith('application/x-yaml')) return 'code';
  if (mime.includes('javascript') || mime.includes('typescript')) return 'code';

  return 'binary';
}

/**
 * Format a byte count as a human-friendly short string ("12.3 KB").
 * Used by the tree row and viewer footer.
 */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  for (const unit of units) {
    value /= 1024;
    if (value < 1024) return `${value.toFixed(value < 10 ? 1 : 0)} ${unit}`;
  }
  return `${value.toFixed(0)} TB`;
}

/**
 * "2 minutes ago", "yesterday", etc. for the modified-at column in the
 * tree. Falls back to a date string when older than 30 days.
 */
export function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  const now = Date.now();
  const diff = now - date.getTime();
  if (Number.isNaN(diff)) return '';
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diff < minute) return 'just now';
  if (diff < hour) return `${Math.floor(diff / minute)} min ago`;
  if (diff < day) return `${Math.floor(diff / hour)} h ago`;
  if (diff < 7 * day) return `${Math.floor(diff / day)} d ago`;
  if (diff < 30 * day) return `${Math.floor(diff / day / 7)} w ago`;
  return date.toLocaleDateString();
}
